/**
 * Zebedo Cart Utilities
 * Reusable AJAX cart functions for adding items and opening the cart drawer
 */

window.ZebedoCart = window.ZebedoCart || {};

/**
 * Add an item to the cart and optionally open the cart drawer
 * @param {string|number} variantId - The variant ID to add
 * @param {number} quantity - Quantity to add (default: 1)
 * @param {boolean} openDrawer - Whether to open the cart drawer after adding (default: true)
 * @param {Object} properties - Optional line item properties
 * @returns {Promise<Object>} - The cart response data
 */
window.ZebedoCart.addToCart = async function(variantId, quantity = 1, openDrawer = true, properties = {}) {
  const body = {
    items: [{
      id: parseInt(variantId),
      quantity: quantity
    }]
  };

  // Add properties if provided
  if (Object.keys(properties).length > 0) {
    body.items[0].properties = properties;
  }

  try {
    // Add item to cart
    const addResponse = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!addResponse.ok) {
      const errorData = await addResponse.json();
      throw new Error(errorData.description || 'Failed to add item to cart');
    }

    const cartData = await addResponse.json();

    // Find the cart drawer (zebedo or default)
    const cartDrawer = document.querySelector('zebedo-cart-drawer') || document.querySelector('cart-drawer');
    
    if (cartDrawer) {
      // Determine the correct section ID based on the drawer type
      const sectionId = cartDrawer.tagName.toLowerCase() === 'zebedo-cart-drawer' 
        ? 'zebedo-cart-drawer' 
        : 'cart-drawer';

      // Fetch updated sections
      const sectionsResponse = await fetch(
        window.Shopify.routes.root + `?sections=${sectionId},cart-icon-bubble`
      );
      const sections = await sectionsResponse.json();

      // Update the cart drawer content
      if (sections[sectionId]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections[sectionId], 'text/html');
        const newDrawerContent = doc.querySelector(cartDrawer.tagName.toLowerCase());
        
        if (newDrawerContent) {
          cartDrawer.innerHTML = newDrawerContent.innerHTML;
        }
      }

      // Update cart icon bubble
      if (sections['cart-icon-bubble']) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          const newBubble = doc.getElementById('cart-icon-bubble');
          if (newBubble) {
            cartIconBubble.innerHTML = newBubble.innerHTML;
          }
        }
      }

      // Open the drawer if requested
      if (openDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    } else {
      // No cart drawer found, redirect to cart page
      if (openDrawer) {
        window.location = window.routes?.cart_url || '/cart';
      }
    }

    // Dispatch custom event for other components to listen to
    document.dispatchEvent(new CustomEvent('zebedo:cart:updated', {
      detail: { cartData }
    }));

    return cartData;

  } catch (error) {
    console.error('ZebedoCart: Error adding to cart:', error);
    
    // Dispatch error event
    document.dispatchEvent(new CustomEvent('zebedo:cart:error', {
      detail: { error: error.message }
    }));

    throw error;
  }
};

/**
 * Update cart icon bubble count
 * Call this after any cart modification to ensure the icon is up to date
 */
window.ZebedoCart.updateCartBubble = async function() {
  try {
    const response = await fetch(window.Shopify.routes.root + '?sections=cart-icon-bubble');
    const sections = await response.json();

    if (sections['cart-icon-bubble']) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = doc.getElementById('cart-icon-bubble');
        if (newBubble) {
          cartIconBubble.innerHTML = newBubble.innerHTML;
        }
      }
    }
  } catch (error) {
    console.error('ZebedoCart: Error updating cart bubble:', error);
  }
};

/**
 * Open the cart drawer without adding items
 */
window.ZebedoCart.openDrawer = function() {
  const cartDrawer = document.querySelector('zebedo-cart-drawer') || document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
  }
};

/**
 * Close the cart drawer
 */
window.ZebedoCart.closeDrawer = function() {
  const cartDrawer = document.querySelector('zebedo-cart-drawer') || document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.close === 'function') {
    cartDrawer.close();
  }
};
